import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useExam } from "../context/ExamContext";
import { registerForExam } from "../api/client";

import AppSidebar from "../pages/AppSidebar";

const CRITERIA = [
  { text: "Hour of exam based on the course" },
  { text: "Only one student in the room" },
  { text: "No mobile phone", bold: true },
  { text: "No unauthorized item" },
  { text: "Do not leave the exam screen once you start the exam" },
  {
    text:
      "Your camera will monitor you during the exam. If any violations occur the exam will be automatically submitted.",
  },
  { text: "Calculator is provided on the screen" },
  { text: "books are not allowed" },
];

const ICONS = [
  "bi-clock",
  "bi-person",
  "bi-phone",
  "bi-shield-x",
  "bi-display",
  "bi-camera-video",
  "bi-calculator",
  "bi-pen",
  "bi-file-earmark-text",
];

export default function ExamCriteria() {
  const navigate = useNavigate();

  const {
    selectedCourse,
    selectedExam,
    setCriteriaAccepted,
    setRegistrationId,
    setPaymentStatus,
    setSchedule,
  } = useExam();

  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /*
   * Home currently stores the selected Django exam
   * using setSelectedCourse().
   *
   * Keep support for both fields so the page remains
   * compatible with the existing flow.
   */

  const exam = selectedExam || selectedCourse;

  if (!exam) {
    navigate("/home");
    return null;
  }

  const examId = exam.id;

  /* ==========================================================
     CONTINUE
     ========================================================== */

  const handleContinue = async () => {
    if (!checked || !examId) {
      return;
    }

    setError("");
    setLoading(true);

    try {
      /*
       * The backend uses get_or_create().
       *
       * First visit:                REGISTERED
       * Existing paid student:      PAID
       * Existing scheduled student: SCHEDULED / VERIFICATION_PENDING
       * Existing ready student:     READY
       * Existing active attempt:    IN_PROGRESS
       */

      const response = await registerForExam(examId);

      const data = response.data;

      const registrationId = data.registration_id;
      const status = data.status;

      setCriteriaAccepted(Boolean(data.criteria_accepted));

      if (registrationId) {
        setRegistrationId(registrationId);
      }

      // A retake is a separate registration: do not reuse previous payment/schedule state.
      if (data.new_attempt_registration === true) {
        setPaymentStatus("Pending");
        setSchedule(null);
      }

      /* REGISTERED: new registration, student must pay first */
      if (status === "REGISTERED" || status === "PAYMENT_PENDING") {
        setPaymentStatus("Pending");
        navigate("/payment");
        return;
      }

      /* PAID: payment done, go directly to scheduling */
      if (status === "PAID") {
        setPaymentStatus("SUCCESS");
        navigate("/schedule");
        return;
      }

      /* SCHEDULED: slot already selected, Schedule page shows state */
      if (status === "SCHEDULED" || status === "VERIFICATION_PENDING") {
        setPaymentStatus("SUCCESS");
        navigate("/schedule");
        return;
      }

      /* READY: verification completed, student can enter the exam */
      if (status === "READY") {
        setPaymentStatus("SUCCESS");
        navigate("/exam");
        return;
      }

      /* IN_PROGRESS: Exam page loads/resumes the existing attempt */
      if (status === "IN_PROGRESS") {
        setPaymentStatus("SUCCESS");
        navigate("/exam");
        return;
      }

      /* Retakes are allowed only after the backend says the result is released. */
      if (status === "SUBMITTED" || status === "AUTO_SUBMITTED") {
        setPaymentStatus("SUCCESS");
        if (data.can_retake === true) {
          navigate("/schedule");
        } else {
          navigate("/submitted");
        }
        return;
      }

      /* TERMINATED: Submitted page shows status per backend rules */
      if (status === "TERMINATED") {
        setPaymentStatus("SUCCESS");
        navigate("/submitted");
        return;
      }

      /* FALLBACK */
      navigate("/payment");
    } catch (err) {
      console.error("EXAM REGISTRATION ERROR:", err);

      const backendData = err?.response?.data;

      let message = "Could not register for this examination.";

      if (backendData && typeof backendData === "object") {
        message = backendData.detail || backendData.message || message;
      }

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  /* ==========================================================
     UI
     ========================================================== */

  return (
    <div className="oes-page-full">
      <div className="oes-home-shell d-flex flex-row overflow-hidden">
        <AppSidebar />

        <div className="flex-grow-1 oes-page container d-flex flex-column align-items-center justify-content-center">
          <div
            className="oes-card p-3 p-md-4 w-100"
            style={{
              maxWidth: 640,
            }}
          >
            {/* TITLE */}

            <h2 className="h6 fw-bold text-center mb-3">
              Examination Criteria
            </h2>

            {/* EXAM NAME */}

            <p className="text-center text-muted small mb-3">
              Course: <strong>{exam.name}</strong>
            </p>

            {/* ERROR */}

            {error && (
              <div className="alert alert-danger small" role="alert">
                {error}
              </div>
            )}

            {/* CRITERIA */}

            <ol className="list-unstyled mb-3">
              {CRITERIA.map((criteria, index) => (
                <li
                  key={criteria.text}
                  className="d-flex align-items-start gap-2 mb-2"
                >
                  <i className={`bi ${ICONS[index]} text-primary mt-1`} />

                  <span className={criteria.bold ? "fw-bold" : ""}>
                    {index + 1}. {criteria.text}
                  </span>
                </li>
              ))}
            </ol>

            {/* ACCEPT */}

            <div className="form-check bg-light rounded p-2 mb-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="agreeCheck"
                checked={checked}
                onChange={(event) => setChecked(event.target.checked)}
              />

              <label className="form-check-label small" htmlFor="agreeCheck">
                I have read and understood all examination criteria.
              </label>
            </div>

            {/* WARNING */}

            <div className="alert alert-warning d-flex align-items-start gap-2 small">
              <i className="bi bi-exclamation-triangle-fill mt-1" />

              <span>
                If any examination criteria are not followed the examination
                will be automatically submitted.
              </span>
            </div>

            {/* CONTINUE */}

            <button
              type="button"
              className="btn btn-primary w-100"
              disabled={!checked || loading}
              onClick={handleContinue}
            >
              {loading
                ? "Checking examination status..."
                : "I AGREE & CONTINUE"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}